import { useCallback, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { type SchedulePreferencesPayload } from '@/features/accounts/api';
import { useFacilities } from '@catalog';
import { errorField, formatError } from '@/shared/api';
import { Schedule } from '@/features/accounts/types';
import ErrorAlert from '@/shared/components/ErrorAlert';
import Stepper, { type StepDefinition } from './Stepper';
import LocationsStep from './steps/LocationsStep';
import DatesStep from './steps/DatesStep';
import AppointmentRulesStep from './steps/AppointmentRulesStep';
import { ConfigurationFormData } from './formData';
import { defaultFormValues, toPreferencesPayload } from './preferences';
import { selectionIncludesAsc } from './ascRules';
import { formFieldFor, limitViolation } from './limits';

interface Props {
  schedule: Schedule;
  countryCode: string;
  preferences?: SchedulePreferencesPayload;
  onSubmit: (preferences: SchedulePreferencesPayload) => Promise<void>;
}

export default function ConfigurationForm({
  schedule,
  countryCode,
  preferences,
  onSubmit,
}: Props) {
  const intl = useIntl();
  const {
    data: facilities = [],
    isLoading: isLoadingFacilities,
    error: facilitiesError,
  } = useFacilities(countryCode);

  const methods = useForm<ConfigurationFormData>({
    defaultValues: defaultFormValues(schedule, preferences),
  });
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = methods;

  const selectedFacilities = useWatch({ control, name: 'preferredFacilities' });
  const showAscFields = selectionIncludesAsc(facilities, selectedFacilities);
  const [rulesEverShown, setRulesEverShown] = useState(showAscFields);
  if (showAscFields && !rulesEverShown) setRulesEverShown(true);

  const showCancellation = !!schedule.current_consular_at;
  const showRulesStep = rulesEverShown || showCancellation;
  const isDisabled = isSubmitting || isLoadingFacilities;

  const [onFinalStep, setOnFinalStep] = useState(false);
  const trackFinalStep = useCallback((final: boolean) => setOnFinalStep(final), []);

  const submit = async (data: ConfigurationFormData) => {
    if (!onFinalStep) return;

    const violation = limitViolation(data);
    if (violation) {
      setError('root', { message: intl.formatMessage({ id: violation.id }, violation.values) });
      return;
    }

    try {
      await onSubmit(toPreferencesPayload(data, facilities));
    } catch (error) {
      const message = formatError(
        error,
        intl.formatMessage({ id: 'accounts.configForm.updateErrorFallback' })
      );

      setError(formFieldFor(errorField(error)) ?? 'root', { message });
    }
  };

  const steps: StepDefinition[] = [
    {
      id: 'locations',
      labelId: 'accounts.configForm.stepLocations',
      fields: ['preferredFacilities'],
      render: () => <LocationsStep facilities={facilities} isLoading={isLoadingFacilities} />,
    },
    {
      id: 'dates',
      labelId: 'accounts.configForm.stepDates',
      fields: ['windows', 'blackouts'],
      render: () => <DatesStep disabled={isDisabled} />,
    },
  ];

  if (showRulesStep) {
    steps.push({
      id: 'rules',
      labelId: 'accounts.configForm.stepRules',
      fields: ['minDaysFromNow', 'maxGapAscConsular', 'minSameDayGap'],
      render: () => (
        <AppointmentRulesStep
          showAscFields={showAscFields}
          showCancellation={showCancellation}
          disabled={isDisabled}
        />
      ),
    });
  }

  if (facilitiesError) {
    return (
      <div>
        <p className="text-sm text-slate-600">
          <FormattedMessage id="accounts.configForm.description" />
        </p>
        <div className="mt-6">
          <ErrorAlert message={intl.formatMessage({ id: 'accounts.configForm.facilitiesError' })} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <p className="text-sm text-slate-600">
        <FormattedMessage id="accounts.configForm.description" />
      </p>

      {errors.root && (
        <div className="mt-4">
          <ErrorAlert message={errors.root.message} />
        </div>
      )}

      <FormProvider {...methods}>
        <form className="mt-4" onSubmit={handleSubmit(submit)} noValidate>
          <Stepper
            steps={steps}
            unlockAll={schedule.date_interest.configured || !!preferences}
            openAt={preferences ? 'last' : 'first'}
            onFinalStepChange={trackFinalStep}
            onSubmit={handleSubmit(submit)}
            disabled={isDisabled}
            submitLabel={
              isSubmitting ? (
                <FormattedMessage id="accounts.configForm.submitSaving" />
              ) : (
                <FormattedMessage id="accounts.configForm.next" />
              )
            }
          />
        </form>
      </FormProvider>
    </div>
  );
}
